import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TrustModuleNav from '../../components/trust/TrustModuleNav';

const TrustAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [toast, setToast] = useState(null);
    const navigate = useNavigate();

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const [formData, setFormData] = useState({
        account_name: '',
        account_number: '',
        bank_name: '',
        bank_branch: '',
        account_type: 'TRUST',
        currency: 'USD',
        opening_balance: '0.00',
        requires_dual_signature: false,
        notes: ''
    });

    useEffect(() => {
        fetchAccounts();
    }, []);

    // Filter accounts based on search and filters
    useEffect(() => {
        let filtered = accounts;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(a =>
                a.account_name.toLowerCase().includes(term) ||
                a.account_number.toLowerCase().includes(term) ||
                a.bank_name.toLowerCase().includes(term)
            );
        }
        if (filterType) {
            filtered = filtered.filter(a => a.account_type === filterType);
        }
        if (filterStatus) {
            filtered = filtered.filter(a =>
                filterStatus === 'active' ? a.is_active : !a.is_active
            );
        }
        setFilteredAccounts(filtered);
    }, [accounts, searchTerm, filterType, filterStatus]);

    const fetchAccounts = async () => {
        setError(null);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/accounts/', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Failed to fetch accounts');

            const data = await response.json();
            setAccounts(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching accounts:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/trust/accounts/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create account');
            }

            setShowModal(false);
            setFormData({
                account_name: '',
                account_number: '',
                bank_name: '',
                bank_branch: '',
                account_type: 'TRUST',
                currency: 'USD',
                opening_balance: '0.00',
                requires_dual_signature: false,
                notes: ''
            });
            showToast('Trust account created successfully');
            fetchAccounts();
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const accountTypes = [
        { value: 'IOLTA', label: 'Interest on Lawyers Trust Account (IOLTA)' },
        { value: 'TRUST', label: 'Client Trust Account' },
        { value: 'ESCROW', label: 'Escrow Account' },
        { value: 'RETAINER', label: 'Retainer Account' }
    ];

    const currencies = [
        { value: 'USD', label: 'US Dollar (USD)' },
        { value: 'ZWL', label: 'Zimbabwe Dollar (ZWL)' },
        { value: 'ZAR', label: 'South African Rand (ZAR)' },
        { value: 'GBP', label: 'British Pound (GBP)' },
        { value: 'EUR', label: 'Euro (EUR)' }
    ];

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-3"></div>
                        <div className="h-7 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
                    </div>
                    <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg animate-pulse"></div>
                                    <div>
                                        <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-2"></div>
                                        <div className="h-3 w-56 bg-gray-100 rounded animate-pulse"></div>
                                    </div>
                                </div>
                                <div className="h-6 w-24 bg-gray-100 rounded animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error && accounts.length === 0) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-6">
                    <nav className="text-sm text-gray-500 mb-2">
                        <Link to="/trust" className="hover:text-blue-600">Trust Accounts</Link>
                        <span className="mx-2">›</span>
                        <span>All Accounts</span>
                    </nav>
                    <h1 className="text-2xl font-bold text-gray-900">Trust Accounts</h1>
                </div>
                <div className="max-w-md mx-auto mt-12">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Accounts</h2>
                        <p className="text-gray-500 mb-6">We couldn't connect to the server. Please try again.</p>
                        <button
                            onClick={() => { setLoading(true); fetchAccounts(); }}
                            className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 p-4 rounded-lg border shadow-lg max-w-sm transition-all ${toast.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    {toast.type === 'error' ? (
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                        <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    )}
                    <p className="text-sm text-gray-700">{toast.message}</p>
                </div>
            )}

            {/* Header */}
            <div className="mb-4">
                <h1 className="text-2xl font-bold text-gray-900">Trust Accounts</h1>
                <p className="text-gray-500 text-sm mt-1">{filteredAccounts.length} account{filteredAccounts.length !== 1 ? 's' : ''} found</p>
            </div>

            {/* Trust module sub-navigation */}
            <TrustModuleNav />

            {/* Action bar */}
            <div className="flex items-center justify-end mb-4">
                <button
                    onClick={() => setShowModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center font-medium transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    New Account
                </button>
            </div>

            {/* Search & Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <div className="relative">
                            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search by name, account number, or bank..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Types</option>
                            {accountTypes.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>
                {(searchTerm || filterType || filterStatus) && (
                    <div className="mt-3 flex items-center gap-2">
                        <span className="text-xs text-gray-500">Filters active:</span>
                        {searchTerm && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                "{searchTerm}"
                                <button onClick={() => setSearchTerm('')} className="hover:text-blue-900">&times;</button>
                            </span>
                        )}
                        {filterType && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                {filterType}
                                <button onClick={() => setFilterType('')} className="hover:text-blue-900">&times;</button>
                            </span>
                        )}
                        {filterStatus && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                {filterStatus}
                                <button onClick={() => setFilterStatus('')} className="hover:text-blue-900">&times;</button>
                            </span>
                        )}
                        <button onClick={() => { setSearchTerm(''); setFilterType(''); setFilterStatus(''); }} className="text-xs text-gray-500 hover:text-gray-700 underline ml-2">
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Accounts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAccounts.length > 0 ? (
                    filteredAccounts.map((account) => {
                        const typeConfig = {
                            IOLTA:    { label: 'IOLTA',    bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-200' },
                            TRUST:    { label: 'Trust',    bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-200' },
                            ESCROW:   { label: 'Escrow',   bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
                            RETAINER: { label: 'Retainer', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
                        };
                        const type = typeConfig[account.account_type] || { label: account.account_type, bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };

                        return (
                        <div
                            key={account.id}
                            role="button"
                            tabIndex={0}
                            aria-label={`View ${account.account_name}`}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => navigate(`/trust/accounts/${account.id}`)}
                            onKeyDown={(e) => e.key === 'Enter' && navigate(`/trust/accounts/${account.id}`)}
                        >
                            {/* Card top stripe by type */}
                            <div className={`h-1 w-full ${type.bg}`} />

                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1 min-w-0 mr-3">
                                        <h3 className="text-lg font-semibold text-gray-900 truncate">{account.account_name}</h3>
                                        <p className="text-sm text-gray-500 mt-0.5">{account.bank_name}</p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${account.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {account.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${type.bg} ${type.text}`}>
                                            {type.label}
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-2.5">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Account No.</span>
                                        <span className="font-mono text-gray-800 text-xs bg-gray-50 px-2 py-0.5 rounded">{account.account_number}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Currency</span>
                                        <span className="text-gray-900 font-medium">{account.currency}</span>
                                    </div>
                                    {account.requires_dual_signature && (
                                        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">
                                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                            </svg>
                                            Dual signature required
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5 pt-4 border-t border-gray-100">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-500">Current Balance</span>
                                        <span className={`text-xl font-bold ${(account.current_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatCurrency(account.current_balance, account.currency)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                                <div className="flex justify-between text-sm">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/trust/transactions?account=${account.id}`);
                                        }}
                                        className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                    >
                                        Transactions
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/trust/accounts/${account.id}`);
                                        }}
                                        className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                    >
                                        View Details →
                                    </button>
                                </div>
                            </div>
                        </div>
                        );
                    })
                ) : (
                    <div className="col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Trust Accounts</h3>
                        <p className="text-gray-500 mb-4">Get started by creating your first trust account</p>
                        <button
                            onClick={() => setShowModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Create Trust Account
                        </button>
                    </div>
                )}
            </div>

            {/* New Account Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">Create New Trust Account</h2>
                                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <p className="text-sm text-blue-800">
                                    <strong>Important:</strong> Trust accounts must be maintained at a financial institution approved for holding client funds.
                                    Ensure you have proper authorization before creating this account.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.account_name}
                                        onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="e.g., Main Client Trust Account"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.bank_name}
                                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="e.g., CBZ Bank"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                                    <input
                                        type="text"
                                        value={formData.bank_branch}
                                        onChange={(e) => setFormData({ ...formData, bank_branch: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="e.g., Harare Central"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.account_number}
                                        onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                                        placeholder="1234567890"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Type *</label>
                                    <select
                                        required
                                        value={formData.account_type}
                                        onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        {accountTypes.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                                    <select
                                        required
                                        value={formData.currency}
                                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        {currencies.map(curr => (
                                            <option key={curr.value} value={curr.value}>{curr.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.opening_balance}
                                            onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.requires_dual_signature}
                                        onChange={(e) => setFormData({ ...formData, requires_dual_signature: e.target.checked })}
                                        className="rounded border-gray-300 text-blue-600"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">
                                        Requires dual signature for withdrawals
                                    </span>
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="3"
                                    placeholder="Any additional notes about this account"
                                ></textarea>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Create Account
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrustAccounts;
